import { TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../../../constants/language';
import { DamagedItemStatus, OrderItemStatus } from '../../../../interfaces/index.types';
import { formatPriceItem } from '../../../../utils';
import { formatHourMonthDayYear } from '../../../../utils/handleDateTime';
import { OrderType } from '../../../Dashboard/order.types';
import { IOrderHistory } from '../../orderHistory.types';
import { useStyles } from './styles';

function TableBodyCustom({ data, rowsPerPage, page, showForSuperAdmin }: { data: IOrderHistory[]; rowsPerPage: number; page: number; showForSuperAdmin: boolean }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const orderTypeKeyMap: Partial<Record<OrderType, keyof typeof LANG_TEXT>> = {
    [OrderType.SPECIAL_ORDERS]: 'SPECIAL_ORDERS',
    [OrderType.SPECIAL_PRE_ORDERS]: 'SPECIAL_PRE_ORDERS',
  };

  const multiOrderIndicator = (row: IOrderHistory) => {
    if (!row.multi_orders?.length) return '-';

    const doneOrders = row.multi_orders.filter(
      (order) => !([OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED] as string[]).includes(order.status)
    );

    return `${doneOrders.length}/${row.multi_orders.length}`;
  };

  return (
    <TableBody>
      {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: IOrderHistory, rowIndex: number, arr) => {
        const isFirstRowOfGroup = rowIndex === 0 || row.id !== arr[rowIndex - 1].id;
        const isLastRowOfGroup = rowIndex === arr.length - 1 || row.id !== arr[rowIndex + 1].id;
        const backgroundColor = row.is_pre_order ? '#FFF4ED' : '#F0F9FF';
        const requestedDeliveryDate = row.requested_delivery_time ? formatHourMonthDayYear(row.requested_delivery_time).date : '-';
        const requestedDeliveryTime = row.requested_delivery_time
          ? row.delivery_request === 'WHEN_ITS_READY'
            ? t(LANG_TEXT.WHEN_ITS_READY)
            : row.delivery_request || formatHourMonthDayYear(row.requested_delivery_time).time
          : '-';

        return (
          <TableRow
            sx={{
              borderTop: isFirstRowOfGroup ? '2px solid #98a2b3' : 'none',
              borderBottom: isLastRowOfGroup ? '2px solid #98a2b3' : 'none',
              borderLeft: '2px solid #98a2b3',
              borderRight: '2px solid #98a2b3',
              backgroundColor: backgroundColor,
            }}
            hover
            role="checkbox"
            tabIndex={-1}
            key={row.id + '-' + rowIndex}
          >
            <TableCell key={0} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {page * rowsPerPage + rowIndex + 1}
              </Typography>
            </TableCell>
            <TableCell key={1} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.id}
              </Typography>
            </TableCell>
            {showForSuperAdmin && (
              <>
                <TableCell key={2} className={classes.TableCell}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.merchant_id}
                  </Typography>
                </TableCell>
                <TableCell key={3} className={classes.TableCell}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.location_id}
                  </Typography>
                </TableCell>
                <TableCell key={4} className={classes.TableCell}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.restaurant_id}
                  </Typography>
                </TableCell>
                <TableCell key={5} className={classes.TableCell}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.restaurant_name}
                  </Typography>
                </TableCell>
              </>
            )}
            <TableCell key={6} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.seat_number}
              </Typography>
            </TableCell>
            <TableCell key={7} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.order_type ? t(LANG_TEXT[orderTypeKeyMap[row.order_type] || (row.order_type.toUpperCase().replace(/-/g, '_') as keyof typeof LANG_TEXT)]) : '-'}
              </Typography>
            </TableCell>
            <TableCell key={8} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {multiOrderIndicator(row)}
              </Typography>
            </TableCell>
            <TableCell key={9} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {requestedDeliveryDate}
              </Typography>
            </TableCell>
            <TableCell key={10} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {requestedDeliveryTime}
              </Typography>
            </TableCell>
            <TableCell key={11} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.rider_name}
              </Typography>
            </TableCell>
            <TableCell key={12} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.completed_time ? formatHourMonthDayYear(row.completed_time).date : '-'}
              </Typography>
            </TableCell>
            <TableCell key={13} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.completed_time ? formatHourMonthDayYear(row.completed_time).time : '-'}
              </Typography>
            </TableCell>
            <TableCell key={14} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.damaged_status}
              </Typography>
            </TableCell>
            <TableCell key={15} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.item_food_id}
              </Typography>
            </TableCell>
            <TableCell key={16} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.sku}
              </Typography>
            </TableCell>
            <TableCell key={17} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.item_customize_settings?.length > 0 ? `${row.item_name} (${row.item_customize_settings.map((item) => item.name).join(', ')})` : row.item_name}
              </Typography>
            </TableCell>
            <TableCell key={18} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.item_quantity}
              </Typography>
            </TableCell>
            <TableCell key={19} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.item_price && `¥ ${formatPriceItem(row.item_price)}`}
              </Typography>
            </TableCell>
            <TableCell key={20} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.ttl_price && `¥ ${formatPriceItem(row.ttl_price)}`}
              </Typography>
            </TableCell>
            <TableCell key={21} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.tax_rate && `${row.tax_rate}%`}
              </Typography>
            </TableCell>
            <TableCell key={22} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.tax_amount && `¥ ${formatPriceItem(row.tax_amount)}`}
              </Typography>
            </TableCell>
            <TableCell key={23} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.before_tax_amount && `¥ ${formatPriceItem(row.before_tax_amount)}`}
              </Typography>
            </TableCell>
            <TableCell key={24} sx={{ padding: 0 }} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {formatHourMonthDayYear(row.order_create_time).date}
              </Typography>
            </TableCell>
            <TableCell key={25} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {formatHourMonthDayYear(row.order_create_time).time}
              </Typography>
            </TableCell>
            {showForSuperAdmin && (
              <>
                <TableCell key={26} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.email || row.email_or_phone || '-'}
                  </Typography>
                </TableCell>
                <TableCell key={27} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.phone || '-'}
                  </Typography>
                </TableCell>
                <TableCell key={28} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.gift_info?.receiver_name || '-'}
                  </Typography>
                </TableCell>
                <TableCell key={29} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.gift_info?.receiver_email || '-'}
                  </Typography>
                </TableCell>
                <TableCell key={30} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                  <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                    {row.gift_info?.receiver_phone || '-'}
                  </Typography>
                </TableCell>
              </>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  );
}

export default TableBodyCustom;
